/**
 * StorageCapBanner
 *
 * A persistent top-of-page banner that surfaces when the backend returns
 * HTTP 507 with `error_code: STORAGE_CAP_EXCEEDED` from any upload endpoint.
 * The api.js global fetch wrapper dispatches a `storage:cap-exceeded` window
 * event; this component listens for it and stays visible until the user
 * dismisses. Once dismissed it stays hidden for the rest of the tab session
 * so admins don't get spammed on every failed upload — the dismissal is
 * kept in-memory only (never persisted) so a page reload re-enables it.
 */
import { useEffect, useState } from 'react';
import { AlertOctagon, X, ExternalLink } from 'lucide-react';

const StorageCapBanner = () => {
  const [visible, setVisible] = useState(false);
  const [detail, setDetail] = useState('');

  useEffect(() => {
    const onCap = (ev) => {
      setDetail((ev && ev.detail) || '');
      setVisible(true);
    };
    window.addEventListener('storage:cap-exceeded', onCap);
    return () => window.removeEventListener('storage:cap-exceeded', onCap);
  }, []);

  if (!visible) return null;

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[100] bg-red-600 text-white shadow-lg"
      data-testid="storage-cap-banner"
    >
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-start gap-3">
        <AlertOctagon className="w-5 h-5 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold">Cloud storage is full — uploads are paused</p>
          <p className="text-xs opacity-90 mt-0.5">
            {detail ||
              'An administrator needs to raise the Backblaze B2 storage cap or delete old files before uploads can resume.'}
          </p>
          <a
            href="https://secure.backblaze.com/b2_buckets.htm"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 mt-1.5 text-xs font-semibold underline underline-offset-2 hover:opacity-80"
            data-testid="storage-cap-banner-link"
          >
            Open Backblaze dashboard <ExternalLink className="w-3 h-3" />
          </a>
        </div>
        <button
          type="button"
          onClick={() => setVisible(false)}
          className="p-1 rounded hover:bg-red-700 transition-colors flex-shrink-0"
          aria-label="Dismiss"
          data-testid="storage-cap-banner-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default StorageCapBanner;
